import type { OrbitalElements } from "../math/orbital_elements";
import type { StateVector } from "../math/state_vector";
import { elementsToState } from "../math/orbital_math";
import { Vector3d } from "../math/vector3d";

export enum BodyType {
  Star = "Star",
  Planet = "Planet",
  DwarfPlanet = "DwarfPlanet",
  Moon = "Moon",
  Asteroid = "Asteroid",
  Comet = "Comet",
  Spacecraft = "Spacecraft",
}

/**
 * Runtime data for a celestial body. Pure TypeScript, no framework dependency.
 */
export interface CelestialBodyData {
  /** Display name */
  name: string;

  /** NAIF/SPICE body ID */
  naifId: number;

  /** Body classification */
  bodyType: BodyType;

  /** Name of the parent body (empty/undefined for the Sun) */
  parentBodyName?: string;

  /** Mass (kg) */
  mass: number;

  /** Gravitational parameter GM (km^3/s^2) */
  gm: number;

  /** Mean radius (km) */
  meanRadius: number;

  /** Surface gravity (m/s^2) */
  surfaceGravity: number;

  /** Escape velocity (m/s) */
  escapeVelocity: number;

  /** Keplerian orbital elements (radians) */
  orbitalElements?: OrbitalElements;

  /** Sphere of influence radius (km) */
  soiRadius: number;

  /** Display color */
  color: { r: number; g: number; b: number };
}

/**
 * Compute state vector of a body at a given Julian Date.
 * For stars, returns zero state. For others, returns state relative to parent.
 */
export function computeBodyState(
  body: CelestialBodyData,
  jd: number,
  parentGM: number,
): StateVector {
  if (body.bodyType === BodyType.Star) {
    return {
      position: Vector3d.Zero,
      velocity: Vector3d.Zero,
      time: jd,
      centralBody: body.name,
    };
  }

  if (!body.orbitalElements) {
    return {
      position: Vector3d.Zero,
      velocity: Vector3d.Zero,
      time: jd,
      centralBody: body.name,
    };
  }

  return elementsToState(body.orbitalElements, parentGM, jd);
}

/**
 * Compute SOI radius from orbital elements and parent body mass.
 * r_SOI = a * (m / M_parent)^(2/5)
 */
export function computeSOIRadius(
  semiMajorAxis: number,
  bodyGM: number,
  parentGM: number,
): number {
  if (parentGM <= 0) return Infinity; // Star has infinite SOI
  const massRatio = bodyGM / parentGM; // GM ratio equals mass ratio
  return semiMajorAxis * Math.pow(massRatio, 0.4);
}
